import React, { useEffect, useState, useRef } from 'react';
import { api } from './shared/utils/api';
import { ArrowUp, ArrowDown, Stable } from './Icons';
import { InfoTooltip } from './InfoTooltip';
import { formatDate } from './dateUtils';

const WEEK_START = new Date('2026-03-30');

function getWeekLabel(weekNum) {
  const d = new Date(WEEK_START);
  d.setDate(d.getDate() + (weekNum - 1) * 7);
  return formatDate(d, 'compact');
}

function LineChart({ data, width = 600, height = 220 }) {
  if (!data || data.length < 2) return (
    <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontSize: 13 }}>
      Rate topics in FLK1 or FLK2 to start building your confidence history.
    </div>
  );

  const padding = { top: 20, right: 20, bottom: 40, left: 40 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  const weeks = data.map(d => d.week);
  const minWeek = Math.min(...weeks);
  const maxWeek = Math.max(...weeks);
  const modules = ['FLK1', 'FLK2'];
  const colors = { FLK1: '#3b82f6', FLK2: '#a855f7' };

  const xScale = w => ((w - minWeek) / Math.max(1, maxWeek - minWeek)) * chartW;
  const yScale = v => chartH - (v / 100) * chartH;

  const makePath = (module) => {
    const points = data.filter(d => d[module] !== undefined);
    if (points.length < 2) return null;
    return points.map((d, i) => `${i === 0 ? 'M' : 'L'} ${xScale(d.week)} ${yScale(d[module])}`).join(' ');
  };

  const makeArea = (module) => {
    const points = data.filter(d => d[module] !== undefined);
    if (points.length < 2) return null;
    const line = points.map((d, i) => `${i === 0 ? 'M' : 'L'} ${xScale(d.week)} ${yScale(d[module])}`).join(' ');
    const close = ` L ${xScale(points[points.length-1].week)} ${chartH} L ${xScale(points[0].week)} ${chartH} Z`;
    return line + close;
  };

  // Y axis labels
  const yLabels = [0, 25, 50, 75, 100];

  // X axis: show every 2-3 weeks
  const xLabels = data.filter((_, i) => i % 2 === 0 || i === data.length - 1);

  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height}`} style={{ overflow: 'visible' }}>
      <g transform={`translate(${padding.left}, ${padding.top})`}>
        {/* Grid lines */}
        {yLabels.map(y => (
          <g key={y}>
            <line x1={0} y1={yScale(y)} x2={chartW} y2={yScale(y)}
              stroke="var(--border)" strokeWidth={0.5} strokeDasharray={y === 0 ? '0' : '3,3'} />
            <text x={-6} y={yScale(y)} textAnchor="end" dominantBaseline="middle"
              fontSize={10} fill="var(--muted)">{y}%</text>
          </g>
        ))}

        {/* X axis labels */}
        {xLabels.map(d => (
          <text key={d.week} x={xScale(d.week)} y={chartH + 16} textAnchor="middle"
            fontSize={9} fill="var(--muted)">{getWeekLabel(d.week)}</text>
        ))}

        {/* Area fills */}
        {modules.map(mod => {
          const area = makeArea(mod);
          if (!area) return null;
          return <path key={mod + '-area'} d={area} fill={colors[mod]} fillOpacity={0.06} />;
        })}

        {/* Lines */}
        {modules.map(mod => {
          const path = makePath(mod);
          if (!path) return null;
          return <path key={mod} d={path} fill="none" stroke={colors[mod]} strokeWidth={2} strokeLinejoin="round" />;
        })}

        {/* Data points */}
        {modules.map(mod =>
          data.filter(d => d[mod] !== undefined).map(d => (
            <g key={mod + d.week}>
              <circle cx={xScale(d.week)} cy={yScale(d[mod])} r={3.5}
                fill={colors[mod]} stroke="var(--bg)" strokeWidth={1.5} />
              <title>W{d.week}: {d[mod]}%</title>
            </g>
          ))
        )}

        {/* 70% target line */}
        <line x1={0} y1={yScale(70)} x2={chartW} y2={yScale(70)}
          stroke="var(--green)" strokeWidth={1} strokeDasharray="5,3" opacity={0.5} />
        <text x={chartW + 4} y={yScale(70)} fontSize={9} fill="var(--green)" dominantBaseline="middle">70%</text>
      </g>
    </svg>
  );
}

export default function ProgressChart() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('both'); // 'both' | 'FLK1' | 'FLK2'
  const containerRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(600);

  useEffect(() => {
    api('/api/progress-history').then(d => { setData(d); setLoading(false); });
  }, []);

  useEffect(() => {
    if (containerRef.current) {
      setChartWidth(containerRef.current.offsetWidth - 16);
    }
  }, []);

  if (loading) return <div className="loading">Loading</div>;

  // Filter data for selected view
  const chartData = data.map(d => {
    const entry = { week: d.week };
    if (view === 'both' || view === 'FLK1') entry.FLK1 = d.FLK1;
    if (view === 'both' || view === 'FLK2') entry.FLK2 = d.FLK2;
    return entry;
  });

  // Latest values
  const latest = data[data.length - 1] || {};
  const flk1Latest = latest.FLK1;
  const flk2Latest = latest.FLK2;

  // Trend: compare last 2 data points
  const prev = data[data.length - 2] || {};
  const flk1Trend = flk1Latest && prev.FLK1 ? flk1Latest - prev.FLK1 : null;
  const flk2Trend = flk2Latest && prev.FLK2 ? flk2Latest - prev.FLK2 : null;

  const trendLabel = (trend) => {
    if (trend === null) return '';
    if (trend > 0) return <><ArrowUp size={12} /> {trend.toFixed(1)}%</>;
    if (trend < 0) return <><ArrowDown size={12} /> {Math.abs(trend).toFixed(1)}%</>;
    return <><Stable size={12} /> stable</>;
  };

  return (
    <div className="progress-page">
      <div className="progress-header">
        <div className="progress-title">Confidence over time <InfoTooltip text="Weekly average confidence across all rated topics, plotted separately for FLK1 and FLK2. Each point is a Sunday snapshot." /></div>
        <div className="progress-subtitle">Average confidence across all topics, tracked weekly. Updates as you rate topics.</div>
      </div>

      <div className="progress-metrics">
        <div className="progress-metric">
          <span className="progress-metric-label" style={{ color: 'var(--flk1)' }}>FLK1 average</span>
          <span className="progress-metric-val">{flk1Latest ? flk1Latest.toFixed(1) + '%' : '—'}</span>
          {flk1Trend !== null && (
            <span className="progress-trend" style={{ color: flk1Trend > 0 ? 'var(--green)' : flk1Trend < 0 ? 'var(--red)' : 'var(--muted)' }}>
              {trendLabel(flk1Trend)}
            </span>
          )}
        </div>
        <div className="progress-metric">
          <span className="progress-metric-label" style={{ color: 'var(--flk2)' }}>FLK2 average</span>
          <span className="progress-metric-val">{flk2Latest ? flk2Latest.toFixed(1) + '%' : '—'}</span>
          {flk2Trend !== null && (
            <span className="progress-trend" style={{ color: flk2Trend > 0 ? 'var(--green)' : flk2Trend < 0 ? 'var(--red)' : 'var(--muted)' }}>
              {trendLabel(flk2Trend)}
            </span>
          )}
        </div>
        <div className="progress-metric">
          <span className="progress-metric-label">Data points</span>
          <span className="progress-metric-val">{data.length}</span>
          <span className="progress-trend" style={{ color: 'var(--muted)' }}>weeks tracked</span>
        </div>
      </div>

      <div className="progress-toolbar">
        <div className="progress-legend">
          {[['FLK1','var(--flk1)'],['FLK2','var(--flk2)']].map(([mod, color]) => (
            <span key={mod} className="progress-legend-item">
              <svg width="24" height="4"><line x1="0" y1="2" x2="24" y2="2" stroke={color} strokeWidth="2.5"/></svg>
              <span style={{ color: 'var(--muted)', fontSize: 12 }}>{mod}</span>
            </span>
          ))}
          <span className="progress-legend-item">
            <svg width="24" height="4"><line x1="0" y1="2" x2="24" y2="2" stroke="var(--green)" strokeWidth="1.5" strokeDasharray="4,2"/></svg>
            <span style={{ color: 'var(--muted)', fontSize: 12 }}>70% target</span>
          </span>
        </div>
        <div className="progress-view-toggle">
          {[['both','Both'],['FLK1','FLK1'],['FLK2','FLK2']].map(([v, label]) => (
            <button key={v} className={`progress-toggle-btn ${view === v ? 'active' : ''}`} onClick={() => setView(v)}>{label}</button>
          ))}
        </div>
      </div>

      <div className="progress-chart-wrap" ref={containerRef}>
        <LineChart data={chartData} width={chartWidth} height={220} />
      </div>

      {data.length < 2 && (
        <div className="progress-tip">
          Rate topics in FLK1 and FLK2. The chart builds as you track your confidence each week.
        </div>
      )}
    </div>
  );
}
